import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import {
  DEAL_TRACKER_BY_USER_TYPE,
  type DealStatus,
  type UserType,
} from '../constants/dealTracker';
import UserProfileView from '../components/admin/UserProfileView';

const PAGE_SIZE = 100;

type DisclosureConsent = 'granted' | 'denied' | 'unknown';

type AdminDealRow = {
  id: string;
  investorId: string;
  startupId: string;
  investorName: string;
  investorEmail: string;
  startupCompanyName: string;
  startupLoginEmail?: string;
  startupThumbnailUrl?: string | null;
  startupDisclosureConsent?: DisclosureConsent;
  investorStatus: DealStatus;
  startupStatus: DealStatus;
  active: boolean;
  createdAt: string | null;
  updatedAt: string | null;
};

type ListDealsResponse = {
  success: boolean;
  deals?: AdminDealRow[];
  error?: string;
  totalActive?: number;
  hasMore?: boolean;
  nextCursor?: string | null;
};

type ProfileFocus = { userId: string; userType: 'startup' | 'investor' };

/** Readable timestamps for admins, e.g. [2026-04-13, 22:10:03] (local time). */
function formatAdminTimestamp(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  const y = d.getFullYear();
  const mo = pad(d.getMonth() + 1);
  const da = pad(d.getDate());
  const h = pad(d.getHours());
  const mi = pad(d.getMinutes());
  const s = pad(d.getSeconds());
  return `[${y}-${mo}-${da}, ${h}:${mi}:${s}]`;
}

function friendlyStatusLabel(userType: UserType, status: DealStatus): string {
  const ui = DEAL_TRACKER_BY_USER_TYPE[userType].ui[status];
  if (ui?.labelCompleted) return ui.labelCompleted;
  if (ui?.label) return ui.label;
  if (status === 'not-contacted') return 'Not started';
  if (status === 'met') return 'Met';
  return status
    .split('-')
    .map(p => (p.length > 0 ? p.charAt(0).toUpperCase() + p.slice(1) : p))
    .join(' ');
}

function statusPillClass(status: DealStatus): string {
  switch (status) {
    case 'deal-signed':
      return 'bg-emerald-900/80 text-emerald-100 border border-emerald-600/50';
    case 'deal-progress':
      return 'bg-amber-900/50 text-amber-100 border border-amber-600/40';
    case 'met':
      return 'bg-sky-900/50 text-sky-100 border border-sky-600/40';
    case 'contacted':
      return 'bg-violet-900/50 text-violet-100 border border-violet-600/40';
    case 'favorited':
      return 'bg-gray-700 text-gray-100 border border-gray-500/50';
    default:
      return 'bg-gray-800 text-gray-400 border border-gray-600/40';
  }
}

function disclosureLabel(c: DisclosureConsent | undefined): {
  title: string;
  detail: string;
  tone: string;
} {
  switch (c) {
    case 'granted':
      return {
        title: 'Disclosure: granted',
        detail:
          'Investor allowed the startup to know they were favorited. Startup-facing flows can show the investor name and related timeline events.',
        tone: 'border-emerald-800/60 bg-emerald-950/30 text-emerald-100/95',
      };
    case 'denied':
      return {
        title: 'Disclosure: declined',
        detail:
          'Investor chose not to disclose yet. The relationship still exists for deal tracking; the startup dashboard may limit investor identity and some CTAs until rules in the product allow otherwise.',
        tone: 'border-amber-800/50 bg-amber-950/25 text-amber-100/90',
      };
    default:
      return {
        title: 'Disclosure: not answered',
        detail:
          'Investor has not completed the consent step (or it is still unknown). Same favorite document still stores both sides’ deal statuses.',
        tone: 'border-gray-600 bg-gray-800/60 text-gray-200',
      };
  }
}

function exportToCsv(deals: AdminDealRow[]) {
  const headers = [
    'id',
    'investorId',
    'investorName',
    'investorEmail',
    'startupId',
    'startupCompanyName',
    'startupLoginEmail',
    'investorStatus',
    'startupStatus',
    'disclosureConsent',
    'createdAt',
    'updatedAt',
  ];
  const escape = (v: string | null | undefined) => {
    if (v == null) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const rows = deals.map(d =>
    [
      d.id,
      d.investorId,
      d.investorName,
      d.investorEmail,
      d.startupId,
      d.startupCompanyName,
      d.startupLoginEmail ?? '',
      d.investorStatus,
      d.startupStatus,
      d.startupDisclosureConsent ?? 'unknown',
      d.createdAt,
      d.updatedAt,
    ]
      .map(escape)
      .join(',')
  );
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `deals-export-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const StartupDealThumbnail: React.FC<{ url?: string | null; companyName: string }> = ({
  url,
  companyName,
}) => {
  if (url) {
    return (
      <div className="h-20 w-32 shrink-0 overflow-hidden rounded-lg border border-gray-600 bg-gray-800 shadow-inner">
        <img
          src={url}
          alt={`${companyName} thumbnail`}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }
  return (
    <div
      className="flex h-20 w-32 shrink-0 items-center justify-center rounded-lg border border-dashed border-gray-600 bg-gray-800/80 px-2 text-center text-[10px] leading-tight text-gray-500"
      aria-hidden
    >
      No thumbnail
    </div>
  );
};

const StatusPill: React.FC<{ userType: UserType; status: DealStatus; caption: string }> = ({
  userType,
  status,
  caption,
}) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-wider text-gray-500">{caption}</span>
    <span
      className={`inline-flex w-fit max-w-full items-center rounded-md px-2.5 py-1 text-xs font-medium ${statusPillClass(status)}`}
    >
      {friendlyStatusLabel(userType, status)}
    </span>
  </div>
);

const AdminDealsPage: React.FC = () => {
  const [deals, setDeals] = useState<AdminDealRow[]>([]);
  const [totalActive, setTotalActive] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [profileFocus, setProfileFocus] = useState<ProfileFocus | null>(null);

  const listDealsFn = useMemo(
    () =>
      httpsCallable<{ limit?: number; startAfterTimestamp?: string }, ListDealsResponse>(
        functions,
        'listDeals'
      ),
    []
  );

  const openDealProfile = useCallback((focus: ProfileFocus) => setProfileFocus(focus), []);
  const closeDealProfile = useCallback(() => setProfileFocus(null), []);

  useEffect(() => {
    if (!profileFocus) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDealProfile();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [profileFocus, closeDealProfile]);

  const loadPage = useCallback(
    async (cursor: string | null, isFirst: boolean) => {
      try {
        isFirst ? setLoading(true) : setPageLoading(true);
        setError(null);
        const result = await listDealsFn({
          limit: PAGE_SIZE,
          ...(cursor != null ? { startAfterTimestamp: cursor } : {}),
        });
        const data = result.data;
        if (data?.success && data.deals) {
          setDeals(data.deals);
          setHasMore(data.hasMore ?? false);
          if (data.totalActive != null) setTotalActive(data.totalActive);
        } else {
          setError(data?.error || 'Failed to load deals');
        }
      } catch (err: unknown) {
        console.error('Error loading deals:', err);
        setError(err instanceof Error ? err.message : 'Failed to load deals');
      } finally {
        isFirst ? setLoading(false) : setPageLoading(false);
      }
    },
    [listDealsFn]
  );

  useEffect(() => {
    void loadPage(null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goNextPage = useCallback(async () => {
    if (!hasMore || deals.length === 0) return;
    const nextCursor = deals[deals.length - 1].createdAt;
    const nextPage = currentPage + 1;
    setCursorStack(prev => {
      const updated = [...prev];
      updated[nextPage] = nextCursor;
      return updated;
    });
    setCurrentPage(nextPage);
    await loadPage(nextCursor, false);
  }, [hasMore, deals, currentPage, loadPage]);

  const goPrevPage = useCallback(async () => {
    if (currentPage === 0) return;
    const prevPage = currentPage - 1;
    setCurrentPage(prevPage);
    await loadPage(cursorStack[prevPage] ?? null, false);
  }, [currentPage, cursorStack, loadPage]);

  const handleExportAll = useCallback(async () => {
    try {
      setExporting(true);
      const result = await listDealsFn({ limit: 3000 });
      const data = result.data;
      if (data?.success && data.deals) {
        exportToCsv(data.deals);
      }
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [listDealsFn]);

  const q = search.trim().toLowerCase();

  const filteredDeals = useMemo(() => {
    if (!q) return deals;
    return deals.filter(
      d =>
        d.investorName.toLowerCase().includes(q) ||
        d.investorEmail.toLowerCase().includes(q) ||
        (d.startupLoginEmail && d.startupLoginEmail.toLowerCase().includes(q)) ||
        d.startupCompanyName.toLowerCase().includes(q) ||
        d.investorId.toLowerCase().includes(q) ||
        d.startupId.toLowerCase().includes(q)
    );
  }, [deals, q]);

  const stats = useMemo(() => {
    const signedEither = deals.filter(
      d => d.investorStatus === 'deal-signed' || d.startupStatus === 'deal-signed'
    ).length;
    const bothSigned = deals.filter(
      d => d.investorStatus === 'deal-signed' && d.startupStatus === 'deal-signed'
    ).length;
    const inProgress = deals.filter(
      d =>
        d.investorStatus === 'deal-progress' ||
        d.startupStatus === 'deal-progress' ||
        d.investorStatus === 'met' ||
        d.startupStatus === 'met'
    ).length;
    return { signedEither, bothSigned, inProgress };
  }, [deals]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#262626' }}
      >
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mb-4" />
          <p className="text-white text-lg">Loading deals…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#262626' }}
      >
        <div className="text-center px-4">
          <p className="text-red-400 text-lg mb-4">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const showingFrom = currentPage * PAGE_SIZE + 1;
  const showingTo = currentPage * PAGE_SIZE + deals.length;

  return (
    <>
      <div className="min-h-screen" style={{ backgroundColor: '#262626' }}>
        <div className="container mx-auto px-4 lg:px-8 py-8 max-w-5xl">
          <header className="mb-8 text-center">
            <h1 className="text-2xl font-semibold text-white tracking-tight">Deal pipeline</h1>
            <p className="text-gray-400 mt-2 text-sm leading-relaxed max-w-2xl mx-auto">
              Each card is one active favorite. Open full startup or investor profiles below; login
              emails are the Firebase Auth addresses for each account.
            </p>
          </header>

          {/* Stats row */}
          {(totalActive != null || deals.length > 0) && (
            <div className="mb-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-xl bg-gray-800/80 border border-gray-700 px-4 py-3 text-center">
                <div className="text-2xl font-bold text-white">{totalActive ?? deals.length}</div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 mt-1">
                  Active pairs
                </div>
              </div>
              <div className="rounded-xl bg-gray-800/80 border border-gray-700 px-4 py-3 text-center">
                <div className="text-2xl font-bold text-emerald-300">{stats.signedEither}</div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 mt-1">
                  Signed (one side+)
                </div>
              </div>
              <div className="rounded-xl bg-gray-800/80 border border-gray-700 px-4 py-3 text-center">
                <div className="text-2xl font-bold text-teal-300">{stats.bothSigned}</div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 mt-1">
                  Both signed
                </div>
              </div>
              <div className="rounded-xl bg-gray-800/80 border border-gray-700 px-4 py-3 text-center">
                <div className="text-2xl font-bold text-amber-200">{stats.inProgress}</div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 mt-1">
                  In motion
                </div>
              </div>
            </div>
          )}

          {/* Search + export toolbar */}
          {deals.length > 0 && (
            <div className="mb-6 flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <label htmlFor="admin-deals-search" className="sr-only">
                  Search deals
                </label>
                <input
                  id="admin-deals-search"
                  type="search"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by name, company, email, or uid…"
                  className="w-full rounded-xl border border-gray-600 bg-gray-900/90 px-4 py-3 text-sm text-white placeholder:text-gray-500 focus:border-emerald-500/60 focus:outline-none focus:ring-1 focus:ring-emerald-500/40"
                />
              </div>
              <button
                type="button"
                onClick={handleExportAll}
                disabled={exporting}
                className="shrink-0 rounded-xl border border-gray-600 bg-gray-800 px-4 py-3 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors disabled:opacity-50"
              >
                {exporting ? 'Exporting…' : 'Export all (CSV)'}
              </button>
            </div>
          )}

          {q && (
            <p className="text-gray-500 text-xs mb-4">
              Showing {filteredDeals.length} match{filteredDeals.length !== 1 ? 'es' : ''} on this
              page for &ldquo;{q}&rdquo;
            </p>
          )}

          {/* Page info */}
          {!q && deals.length > 0 && totalActive != null && (
            <p className="text-gray-500 text-xs mb-4">
              Showing {showingFrom}–{showingTo} of {totalActive} active deals
            </p>
          )}

          {deals.length === 0 ? (
            <div className="rounded-2xl bg-gray-800/90 border border-gray-700 p-10 text-center">
              <p className="text-gray-300 text-lg mb-1">No active deals yet</p>
              <p className="text-gray-500 text-sm">
                When investors add startups to favorites, they will show up here with live status.
              </p>
            </div>
          ) : filteredDeals.length === 0 ? (
            <div className="rounded-2xl bg-gray-800/90 border border-gray-700 p-8 text-center text-gray-400 text-sm">
              No deals match your search on this page.
            </div>
          ) : (
            <ul className={`space-y-6 ${pageLoading ? 'opacity-50 pointer-events-none' : ''}`}>
              {filteredDeals.map(d => {
                const disc = disclosureLabel(d.startupDisclosureConsent);
                return (
                  <li
                    key={d.id}
                    className="rounded-2xl border border-gray-700 bg-gray-900/60 p-5 sm:p-6 shadow-lg shadow-black/20"
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
                      {/* Investor column */}
                      <div className="rounded-xl border border-sky-900/40 bg-sky-950/20 p-4 flex flex-col">
                        <div className="flex items-center justify-between gap-2 mb-3">
                          <span className="text-xs font-bold uppercase tracking-widest text-sky-300/90">
                            Investor
                          </span>
                          <StatusPill
                            userType="investor"
                            status={d.investorStatus}
                            caption="Their tracker"
                          />
                        </div>
                        <p className="text-white font-semibold text-lg leading-tight">
                          {d.investorName}
                        </p>
                        <p className="text-[11px] uppercase tracking-wide text-gray-500 mt-3 mb-0.5">
                          Login email
                        </p>
                        <a
                          href={
                            d.investorEmail && d.investorEmail !== '—'
                              ? `mailto:${d.investorEmail}`
                              : undefined
                          }
                          className={`text-sm font-mono break-all ${d.investorEmail && d.investorEmail !== '—' ? 'text-sky-200 hover:underline' : 'text-gray-500'}`}
                        >
                          {d.investorEmail}
                        </a>
                        <button
                          type="button"
                          onClick={() =>
                            openDealProfile({ userId: d.investorId, userType: 'investor' })
                          }
                          className="mt-4 w-full sm:w-auto rounded-lg border border-sky-700/60 bg-sky-900/30 px-4 py-2 text-sm font-medium text-sky-100 hover:bg-sky-900/50 transition-colors"
                        >
                          View full investor profile
                        </button>
                      </div>

                      {/* Startup column */}
                      <div className="rounded-xl border border-emerald-900/40 bg-emerald-950/15 p-4 flex flex-col">
                        <div className="flex items-center justify-between gap-2 mb-3">
                          <span className="text-xs font-bold uppercase tracking-widest text-emerald-300/90">
                            Startup
                          </span>
                          <StatusPill
                            userType="startup"
                            status={d.startupStatus}
                            caption="Their tracker"
                          />
                        </div>
                        <div className="flex gap-4 items-start">
                          <StartupDealThumbnail
                            url={d.startupThumbnailUrl}
                            companyName={d.startupCompanyName}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-white font-semibold text-lg leading-tight">
                              {d.startupCompanyName}
                            </p>
                            <p className="text-[11px] uppercase tracking-wide text-gray-500 mt-3 mb-0.5">
                              Founder login email
                            </p>
                            <a
                              href={
                                d.startupLoginEmail && d.startupLoginEmail !== '—'
                                  ? `mailto:${d.startupLoginEmail}`
                                  : undefined
                              }
                              className={`text-sm font-mono break-all ${d.startupLoginEmail && d.startupLoginEmail !== '—' ? 'text-emerald-200 hover:underline' : 'text-gray-500'}`}
                            >
                              {d.startupLoginEmail ?? '—'}
                            </a>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            openDealProfile({ userId: d.startupId, userType: 'startup' })
                          }
                          className="mt-4 w-full sm:w-auto rounded-lg border border-emerald-700/60 bg-emerald-900/25 px-4 py-2 text-sm font-medium text-emerald-100 hover:bg-emerald-900/40 transition-colors"
                        >
                          View full startup profile
                        </button>
                      </div>
                    </div>

                    <div className={`mt-4 rounded-lg border px-3 py-2.5 text-xs ${disc.tone}`}>
                      <p className="font-semibold">{disc.title}</p>
                      <p className="mt-1 text-[11px] leading-snug opacity-95">{disc.detail}</p>
                    </div>

                    <div className="mt-5 pt-4 border-t border-gray-800 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500 text-xs block mb-0.5">Created</span>
                        <span className="text-gray-200 font-mono text-xs">
                          {formatAdminTimestamp(d.createdAt)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 text-xs block mb-0.5">Last updated</span>
                        <span className="text-gray-200 font-mono text-xs">
                          {formatAdminTimestamp(d.updatedAt)}
                        </span>
                      </div>
                    </div>

                    <details className="mt-4 group">
                      <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-300 list-none flex items-center gap-1 [&::-webkit-details-marker]:hidden">
                        <span className="inline-block transition group-open:rotate-90 text-gray-600">
                          ▸
                        </span>
                        Technical IDs (Firestore)
                      </summary>
                      <div className="mt-2 rounded-lg bg-black/30 p-3 font-mono text-[11px] text-gray-500 break-all space-y-1">
                        <div>
                          <span className="text-gray-600">Document:</span> {d.id}
                        </div>
                        <div>
                          <span className="text-gray-600">Investor uid:</span> {d.investorId}
                        </div>
                        <div>
                          <span className="text-gray-600">Startup uid:</span> {d.startupId}
                        </div>
                      </div>
                    </details>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Pagination controls */}
          {!q && (currentPage > 0 || hasMore) && (
            <div className="mt-8 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={goPrevPage}
                disabled={currentPage === 0 || pageLoading}
                className="rounded-lg border border-gray-600 bg-gray-800 px-5 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors disabled:opacity-40"
              >
                ← Previous
              </button>
              <span className="text-xs text-gray-500">
                Page {currentPage + 1}
                {totalActive != null ? ` of ${Math.ceil(totalActive / PAGE_SIZE)}` : ''}
              </span>
              <button
                type="button"
                onClick={goNextPage}
                disabled={!hasMore || pageLoading}
                className="rounded-lg border border-gray-600 bg-gray-800 px-5 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>

      {profileFocus &&
        createPortal(
          <div
            className="fixed inset-0 z-[200000] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
            onClick={e => {
              if (e.target === e.currentTarget) closeDealProfile();
            }}
            role="dialog"
            aria-modal="true"
          >
            <div className="relative w-full max-w-3xl rounded-2xl overflow-hidden shadow-2xl ring-1 ring-gray-700 bg-dark-950">
              <UserProfileView
                userId={profileFocus.userId}
                userType={profileFocus.userType as 'startup' | 'investor'}
                onClose={closeDealProfile}
                hideProfileHeader
                adminCompact
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default AdminDealsPage;
